import { PaymentStrategy } from '@ctct/state-server-layer';

import type { Controller, Listeners } from '../helpers';
import { controllerHelpers } from '../helpers';
import { billingInfoFormEventBus, paymentMethodsEventBus } from '../managers';
import type {
  BillingInfoFormManager,
  BillingInfoFormValues,
  OrderSummaryManager,
  PaymentButtonManager,
  PaymentMethodsManager,
  TaxInfo,
} from '../managers';

export class BillingInfoFormController implements Controller {
  public listeners: Listeners;

  constructor(
    private billingInfoFormManager: BillingInfoFormManager,
    private paymentMethodsManager: PaymentMethodsManager,
    private orderSummaryManager?: OrderSummaryManager,
    private paymentButtonManager?: PaymentButtonManager
  ) {
    const eventConnections = [
      billingInfoFormEventBus.bindTo('taxInfoUpdated', this.onTaxInfoUpdated),
      billingInfoFormEventBus.bindTo('isVatEuCountry', this.onIsVatEuCountry),
      paymentMethodsEventBus.bindTo(
        'partialBillingInfoFormDataFromApplePay',
        this.updateBillingInfoFromUsingApplePayData
      ),
      billingInfoFormEventBus.bindTo('countryUpdated', this.onCountryChange),
    ];

    this.listeners = controllerHelpers.createListeners(eventConnections);
  }

  private onTaxInfoUpdated = (taxInfo: TaxInfo) => {
    if (this.orderSummaryManager) {
      this.orderSummaryManager.getApiWhenReady().then((api) => api.updateCustomerPartially(taxInfo));
    }
  };

  private onIsVatEuCountry = (isVatEu: boolean) => {
    this.orderSummaryManager?.getApiWhenReady().then((api) => api.setIsVatEuCountry(isVatEu));
  };

  private updateBillingInfoFromUsingApplePayData = (partialBillingInfo: Partial<BillingInfoFormValues>) => {
    if (this.orderSummaryManager) {
      this.orderSummaryManager.getApiWhenReady().then((api) => {
        api.setTaxUpdateRequested(false);
      });
    }

    this.billingInfoFormManager.getApiWhenReady().then((api) => {
      api.setFormValuesIfCurrentlyEmpty({
        newValues: partialBillingInfo,
      });

      api.updateMissingFields([]);
    });
  };

  private onCountryChange = async (countryCode: string, isB2bCountry: boolean) => {
    if (this.orderSummaryManager) {
      const orderSummary = await this.orderSummaryManager.getApiWhenReady();

      orderSummary.updateB2bTooltipVisibility(isB2bCountry);
    }

    const paymentMethods = await this.paymentMethodsManager?.getApiWhenReady();

    if (paymentMethods?.paymentStrategy === PaymentStrategy.Stripe) {
      paymentMethods.updateBillingCountry(countryCode);
    }

    if (this.paymentButtonManager) {
      const paymentButtonApi = await this.paymentButtonManager.getApiWhenReady();

      paymentButtonApi.setBillingCountryCode(countryCode);
    }
  };
}
